import {
  CapacityPresetId,
  ForecastSegmentKind,
  ProjectDetailTab,
  ProjectLifecycleStatus,
  ProjectTaskWriteScope,
  SORT_MODE
} from 'enums';
import { DEFAULT_PROJECT_STATUS_COLORS } from './color';

export const PROJECT_NAME_MAX_LENGTH = 80;

export const PROJECT_DETAILS_HIDDEN_SORT_MODES = [
  SORT_MODE.CHRONOLOGICAL
] as const;

/**
 * The rule the to-do API currently enforces for project task writes.
 *
 * BLOCKED ON Focus-Bear/backend#2413 — once that ships, change this to
 * `ProjectTaskWriteScope.PROJECT_MEMBER`. That single edit drops the
 * "only tasks you created" banner and the owner-scoped refusal message,
 * because every task surface reads the rule through
 * `getProjectTaskPermissions`.
 */
export const PROJECT_TASK_WRITE_SCOPE: ProjectTaskWriteScope =
  ProjectTaskWriteScope.TASK_OWNER;

export const MEMBER_AVATAR_FALLBACK_INITIAL = 'U';
export const MEMBER_OWNER_AVATAR_INITIAL = 'O';

export const MEMBER_AVATAR_COLOR_CLASSES = [
  'bg-sky-600 text-white',
  'bg-violet-600 text-white',
  'bg-emerald-600 text-white',
  'bg-amber-600 text-white',
  'bg-rose-600 text-white',
  'bg-teal-600 text-white',
  'bg-indigo-600 text-white'
] as const;

export const MEMBER_OWNER_AVATAR_COLOR_CLASS = 'bg-blue-600 text-white';

export const MEMBER_CURRENT_USER_AVATAR_CLASS =
  'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-dark-500';

export const PROJECT_INVITE_STATUS_ICON_CLASS = {
  LOADING:
    'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-dark-500',
  SUCCESS:
    'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  ERROR: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
} as const;

export const PROJECT_DUE_DATE_INPUT_ID = 'project-due-date';

export const CAPACITY_SETTINGS_TITLE_ID = 'capacity-settings-title';

export const READY_FOR_HUMAN_REVIEW_COLOR = '#F59E0B';

export const PROJECT_STATUS_I18N_KEYS = {
  readyForHumanReview: 'project_statuses.ready_for_human_review'
} as const;

export const PROJECT_DETAIL_TAB_LABEL_KEYS: Record<ProjectDetailTab, string> = {
  [ProjectDetailTab.TASKS]: 'project.tabs.tasks',
  [ProjectDetailTab.NOTES]: 'project.tabs.notes',
  [ProjectDetailTab.MEMBERS]: 'project.tabs.members',
  [ProjectDetailTab.SETTINGS]: 'project.tabs.settings'
};

export const PROJECT_LIFECYCLE_STATUS_OPTIONS: ProjectLifecycleStatus[] = [
  ProjectLifecycleStatus.ACTIVE,
  ProjectLifecycleStatus.COMPLETED,
  ProjectLifecycleStatus.ARCHIVED
];

export const PROJECT_LIFECYCLE_STATUS_LABEL_KEYS: Record<
  ProjectLifecycleStatus,
  string
> = {
  [ProjectLifecycleStatus.ACTIVE]: 'project.status_active',
  [ProjectLifecycleStatus.COMPLETED]: 'project.status_completed',
  [ProjectLifecycleStatus.ARCHIVED]: 'project.status_archived'
};

export const PROJECT_LIFECYCLE_STATUS_TOOLTIP_KEYS: Partial<
  Record<ProjectLifecycleStatus, string>
> = {
  [ProjectLifecycleStatus.COMPLETED]:
    'project.lifecycle_status_tooltip_completed',
  [ProjectLifecycleStatus.ARCHIVED]: 'project.lifecycle_status_tooltip_archived'
};

export const PROJECT_LIFECYCLE_BADGE_CLASS: Partial<
  Record<ProjectLifecycleStatus, string>
> = {
  [ProjectLifecycleStatus.COMPLETED]:
    'bg-semantic-success/15 text-semantic-success dark:bg-semantic-success/20 dark:text-secondary-600',
  [ProjectLifecycleStatus.ARCHIVED]:
    'bg-secondary-100 text-gray-600 dark:bg-secondary-dark-300 dark:text-secondary-600'
};

export const PROJECT_LIFECYCLE_CONFIRM_KEYS: Record<
  'markComplete' | 'archive' | 'restore',
  { message: string; confirm: string }
> = {
  markComplete: {
    message: 'project.mark_complete_confirm',
    confirm: 'project.mark_complete'
  },
  archive: {
    message: 'project.archive_confirm',
    confirm: 'project.archive_project'
  },
  restore: {
    message: 'project.restore_confirm',
    confirm: 'project.restore_project'
  }
};

export type ProjectsListEmptyVariant =
  'active_zero' | 'search' | 'completed' | 'archived';

export const PROJECT_LIST_EMPTY_STATE_KEYS: Record<
  ProjectsListEmptyVariant,
  { title: string; description: string; action?: string }
> = {
  active_zero: {
    title: 'project.no_projects',
    description: 'project.create_first_project',
    action: 'project.new_project'
  },
  search: {
    title: 'project.no_matching_projects',
    description: 'project.try_different_search',
    action: 'project.clear_search'
  },
  completed: {
    title: 'project.no_completed_projects',
    description: 'project.no_completed_projects_hint',
    action: 'project.show_active_projects'
  },
  archived: {
    title: 'project.no_archived_projects',
    description: 'project.no_archived_projects_hint',
    action: 'project.show_active_projects'
  }
};

/** Tasks | Notes page views. Not Members/Settings overlays. */
export const PROJECT_DETAIL_PAGE_VIEWS = [
  ProjectDetailTab.TASKS,
  ProjectDetailTab.NOTES
] as const;

export const PROJECT_PAGE_VIEWS_ARIA_KEY = 'project.page_views_aria';

export type ProjectDetailPageView = (typeof PROJECT_DETAIL_PAGE_VIEWS)[number];

export const PROJECT_SETTINGS_DRAWER_WIDTH = 520;
export const PROJECT_SETTINGS_DESCRIPTION_ROWS = 6;
export const PROJECT_NOTES_LIST_WIDTH = 320;

/** Suffix keys for `portfolio.weekday_${key}` i18n entries (Mon–Sun, index 0–6). */
export const PORTFOLIO_WEEKDAY_I18N_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
] as const;

export type PortfolioWeekdayI18nKey =
  (typeof PORTFOLIO_WEEKDAY_I18N_KEYS)[number];

export const CAPACITY_HORIZON_DAYS = {
  WEEK: 7,
  TWO_WEEKS: 14,
  FOUR_WEEKS: 28,
  TWO_MONTHS: 60,
  MAX: 90
} as const;

export const PORTFOLIO_CAPACITY = {
  MIN_HORIZON_DAYS: 1,
  DEFAULT_HORIZON_DAYS: 30,
  MAX_HORIZON_DAYS: 365,
  MAX_HOURS_PER_DAY: 24,
  MIN_HOURS_PER_DAY: 0,
  /** BE portfolio/settings + calendar events `days` max. */
  SETTINGS_MAX_HORIZON_DAYS: CAPACITY_HORIZON_DAYS.MAX
} as const;

/** Planning horizon choices in capacity settings (week → BE calendar max). */
export const CAPACITY_HORIZON_DAY_OPTIONS = [
  CAPACITY_HORIZON_DAYS.WEEK,
  CAPACITY_HORIZON_DAYS.TWO_WEEKS,
  CAPACITY_HORIZON_DAYS.FOUR_WEEKS,
  CAPACITY_HORIZON_DAYS.TWO_MONTHS,
  CAPACITY_HORIZON_DAYS.MAX
] as const;

/** Local work-window used when suggesting a gross daily work budget. */
export const CAPACITY_WORK_WINDOW = {
  START_HOUR: 9,
  END_HOUR: 17
} as const;

export const CAPACITY_PRESET_HOURS = {
  FULL_DAY: 8,
  MEDIUM_DAY: 6,
  LIGHT_DAY: 4,
  OFF: 0
} as const;

/** Sat / Sun indexes in Mon–Sun `weekly_capacity_hours`. */
export const CAPACITY_WEEKEND_DAY_INDEXES = [5, 6] as const;

const workweekHours = (
  weekdayHours: number
): readonly [number, number, number, number, number, number, number] => [
  weekdayHours,
  weekdayHours,
  weekdayHours,
  weekdayHours,
  weekdayHours,
  CAPACITY_PRESET_HOURS.OFF,
  CAPACITY_PRESET_HOURS.OFF
];

/** Static Mon–Sun templates for quick-setup presets. */
export const CAPACITY_PRESET_TEMPLATES: Record<
  CapacityPresetId,
  readonly [number, number, number, number, number, number, number]
> = {
  [CapacityPresetId.FULL_WEEK_8]: [
    CAPACITY_PRESET_HOURS.FULL_DAY,
    CAPACITY_PRESET_HOURS.FULL_DAY,
    CAPACITY_PRESET_HOURS.FULL_DAY,
    CAPACITY_PRESET_HOURS.FULL_DAY,
    CAPACITY_PRESET_HOURS.FULL_DAY,
    CAPACITY_PRESET_HOURS.FULL_DAY,
    CAPACITY_PRESET_HOURS.FULL_DAY
  ],
  [CapacityPresetId.WORKWEEK_8]: workweekHours(CAPACITY_PRESET_HOURS.FULL_DAY),
  [CapacityPresetId.WORKWEEK_6]: workweekHours(
    CAPACITY_PRESET_HOURS.MEDIUM_DAY
  ),
  [CapacityPresetId.LIGHT_4]: workweekHours(CAPACITY_PRESET_HOURS.LIGHT_DAY),
  [CapacityPresetId.FULL_WEEK_4]: [
    CAPACITY_PRESET_HOURS.LIGHT_DAY,
    CAPACITY_PRESET_HOURS.LIGHT_DAY,
    CAPACITY_PRESET_HOURS.LIGHT_DAY,
    CAPACITY_PRESET_HOURS.LIGHT_DAY,
    CAPACITY_PRESET_HOURS.LIGHT_DAY,
    CAPACITY_PRESET_HOURS.LIGHT_DAY,
    CAPACITY_PRESET_HOURS.LIGHT_DAY
  ]
};

export const CAPACITY_PRESET_ORDER: readonly CapacityPresetId[] = [
  CapacityPresetId.FULL_WEEK_8,
  CapacityPresetId.WORKWEEK_8,
  CapacityPresetId.WORKWEEK_6,
  CapacityPresetId.LIGHT_4,
  CapacityPresetId.FULL_WEEK_4
];

/** Primary preset cards in capacity Quick setup (Option B). */
export const CAPACITY_PRESET_QUICK_ORDER = [
  CapacityPresetId.WORKWEEK_8,
  CapacityPresetId.LIGHT_4,
  CapacityPresetId.FULL_WEEK_8
] as const satisfies readonly CapacityPresetId[];

export type CapacityPresetQuickId =
  (typeof CAPACITY_PRESET_QUICK_ORDER)[number];

export const CAPACITY_PRESET_LABEL_KEYS: Record<CapacityPresetId, string> = {
  [CapacityPresetId.FULL_WEEK_8]: 'portfolio.capacity_preset_full_week_8',
  [CapacityPresetId.WORKWEEK_8]: 'portfolio.capacity_preset_workweek_8',
  [CapacityPresetId.WORKWEEK_6]: 'portfolio.capacity_preset_workweek_6',
  [CapacityPresetId.LIGHT_4]: 'portfolio.capacity_preset_light_4',
  [CapacityPresetId.FULL_WEEK_4]: 'portfolio.capacity_preset_full_week_4'
};

export const CAPACITY_PRESET_QUICK_LABEL_KEYS: Record<
  CapacityPresetQuickId,
  string
> = {
  [CapacityPresetId.WORKWEEK_8]: 'portfolio.capacity_preset_quick_work_8',
  [CapacityPresetId.LIGHT_4]: 'portfolio.capacity_preset_quick_light_4',
  [CapacityPresetId.FULL_WEEK_8]: 'portfolio.capacity_preset_quick_full_8'
};

export const CAPACITY_PRESET_QUICK_SUBTITLE_KEYS: Record<
  CapacityPresetQuickId,
  string
> = {
  [CapacityPresetId.WORKWEEK_8]:
    'portfolio.capacity_preset_quick_work_8_subtitle',
  [CapacityPresetId.LIGHT_4]:
    'portfolio.capacity_preset_quick_light_4_subtitle',
  [CapacityPresetId.FULL_WEEK_8]:
    'portfolio.capacity_preset_quick_full_8_subtitle'
};

/** Daily-pace durations are rounded to (and floored at) this many minutes. */
export const RECOMMENDED_PACE_ROUND_MINUTES = 5;

/** Max rows shown in the Recommended today plan before "+N more". */
export const RECOMMENDED_TODAY_VISIBLE_LIMIT = 3;

/** Max project names listed in the overcommitted banner before "+N more". */
export const OVER_COMMITTED_BANNER_NAME_LIMIT = 3;

/**
 * Max backburner projects promoted into "start now" when that section
 * would otherwise be empty. Prefer due-dated ON_TRACK (buffer against
 * deadlines); if none exist, fall back to HIGH/CRITICAL importance.
 */
export const PORTFOLIO_ENCOURAGED_START_NOW_LIMIT = 3;

/** Max cards shown in This-week Backburner (hard cap, no "+N more"). */
export const PORTFOLIO_BACK_BURNER_VISIBLE_LIMIT = 6;

/** Max cards shown in This-week Start now (hard cap, no "+N more"). */
export const PORTFOLIO_START_NOW_VISIBLE_LIMIT = 6;

/** Projects without a deadline or risk data sort after all ranked projects. */
export const PORTFOLIO_NO_RISK_RANK = -1;

export const PORTFOLIO_I18N_KEYS = {
  due: 'portfolio.due',
  noDueDate: 'portfolio.no_due_date',
  requiredHours: 'portfolio.required_hours',
  recommendedDaily: 'portfolio.recommended_daily',
  recommendedDailyTooltip: 'portfolio.recommended_daily_tooltip',
  recommendedTodayTitle: 'portfolio.recommended_today_title',
  recommendedTodayItem: 'portfolio.recommended_today_item',
  recommendedTodayTotal: 'portfolio.recommended_today_total',
  recommendedTodayMore: 'portfolio.recommended_today_more',
  capacityHoursAria: 'portfolio.capacity_hours_aria',
  nextTask: 'portfolio.next_task',
  nextTaskEmpty: 'portfolio.next_task_empty',
  topScoreTooltip: 'portfolio.top_score_tooltip',
  setDueDate: 'portfolio.set_due_date',
  askForExtensionSection: 'portfolio.ask_for_extension_section',
  startNowSection: 'portfolio.start_now_section',
  backBurnerSection: 'portfolio.backburner_section',
  overCommittedTitle: 'portfolio.overcommitted_title',
  overCommittedDescription: 'portfolio.overcommitted_description',
  overCommittedDescriptionFallback:
    'portfolio.overcommitted_description_fallback',
  overCommittedAndMore: 'portfolio.overcommitted_and_more',
  capacitySettings: 'portfolio.capacity_settings',
  capacitySettingsSubtitle: 'portfolio.capacity_settings_subtitle',
  capacitySettingsTooltip: 'portfolio.capacity_settings_tooltip',
  refreshTooltip: 'portfolio.refresh_tooltip',
  loadingSettings: 'portfolio.loading_settings',
  weeklyCapacity: 'portfolio.weekly_capacity',
  capacityQuickSetup: 'portfolio.capacity_quick_setup',
  capacityWeekendsToggle: 'portfolio.capacity_weekends_toggle',
  capacityAutoToggle: 'portfolio.capacity_auto_toggle',
  capacityAutoHint: 'portfolio.capacity_auto_hint',
  capacityAutoSummaryLabel: 'portfolio.capacity_auto_summary_label',
  capacityAutoSummaryHint: 'portfolio.capacity_auto_summary_hint',
  capacityHoursShort: 'portfolio.capacity_hours_short',
  capacityPlanningHorizonHint: 'portfolio.capacity_planning_horizon_hint',
  capacityBreakdownSleep: 'portfolio.capacity_breakdown_sleep',
  capacityBreakdownEvents: 'portfolio.capacity_breakdown_events',
  capacityBreakdownHabits: 'portfolio.capacity_breakdown_habits',
  capacityBreakdownAvailable: 'portfolio.capacity_breakdown_available',
  capacityPlanningHorizon: 'portfolio.capacity_planning_horizon',
  capacityHorizonDays: 'portfolio.capacity_horizon_days',
  capacityWeeklyTotal: 'portfolio.capacity_weekly_total',
  capacityCouldNotLoad: 'portfolio.capacity_could_not_load',
  capacityRetry: 'portfolio.capacity_retry',
  capacityLoadingAria: 'portfolio.capacity_loading_aria',
  capacityTemplates: 'portfolio.capacity_templates',
  dueDate: 'portfolio.due_date',
  importance: 'portfolio.importance',
  settingsSaved: 'portfolio.settings_saved',
  couldNotSaveSettings: 'portfolio.could_not_save_settings',
  viewFocus: 'portfolio.view_focus',
  viewAllProjects: 'portfolio.view_all_projects',
  viewForecast: 'portfolio.view_forecast',
  browseAllProjects: 'portfolio.browse_all_projects',
  focusEmptyTitle: 'portfolio.focus_empty_title',
  focusEmptyDescription: 'portfolio.focus_empty_description',
  focusLoadingAria: 'portfolio.focus_loading_aria',
  forecastTitle: 'portfolio.forecast_title',
  forecastSubtitle: 'portfolio.forecast_subtitle',
  forecastLoadingAria: 'portfolio.forecast_loading_aria',
  forecastEmptyTitle: 'portfolio.forecast_empty_title',
  forecastEmptyDescription: 'portfolio.forecast_empty_description',
  forecastAvailableLabel: 'portfolio.forecast_available_label',
  forecastRequiredLabel: 'portfolio.forecast_required_label',
  forecastCrunchDay: 'portfolio.forecast_crunch_day',
  forecastFreeLabel: 'portfolio.forecast_free_label',
  forecastSleepLabel: 'portfolio.forecast_sleep_label',
  forecastEventsLabel: 'portfolio.forecast_events_label',
  forecastRoutinesLabel: 'portfolio.forecast_routines_label',
  forecastNoWorkPlanned: 'portfolio.forecast_no_work_planned',
  forecastHoursShort: 'portfolio.forecast_hours_short'
} as const;

/** Ordered day-capacity breakdown rows: i18n key + CurrentWeekDayCapacity field. */
export const CAPACITY_BREAKDOWN_ROWS = [
  {
    labelKey: PORTFOLIO_I18N_KEYS.capacityBreakdownSleep,
    valueKey: 'sleep_hours'
  },
  {
    labelKey: PORTFOLIO_I18N_KEYS.capacityBreakdownEvents,
    valueKey: 'events_hours'
  },
  {
    labelKey: PORTFOLIO_I18N_KEYS.capacityBreakdownHabits,
    valueKey: 'habits_hours'
  },
  {
    labelKey: PORTFOLIO_I18N_KEYS.capacityBreakdownAvailable,
    valueKey: 'available_hours'
  }
] as const;

/** Fixed-segment legend for the time-forecast tab (dynamic capacity mode). */
export const FORECAST_FIXED_LEGEND = [
  {
    kind: ForecastSegmentKind.SLEEP,
    labelKey: PORTFOLIO_I18N_KEYS.forecastSleepLabel,
    colorClass: 'bg-indigo-300 dark:bg-indigo-700'
  },
  {
    kind: ForecastSegmentKind.EVENTS,
    labelKey: PORTFOLIO_I18N_KEYS.forecastEventsLabel,
    colorClass: 'bg-rose-400 dark:bg-rose-500'
  },
  {
    kind: ForecastSegmentKind.ROUTINES,
    labelKey: PORTFOLIO_I18N_KEYS.forecastRoutinesLabel,
    colorClass: 'bg-violet-400 dark:bg-violet-500'
  },
  {
    kind: ForecastSegmentKind.FREE,
    labelKey: PORTFOLIO_I18N_KEYS.forecastFreeLabel,
    colorClass: 'bg-secondary-600 dark:bg-secondary-900'
  }
] as const;

/** i18n keys for fixed forecast bar segments; project slices use `segment.label`. */
export const FORECAST_SEGMENT_LABEL_KEYS: Record<
  ForecastSegmentKind,
  string | null
> = {
  [ForecastSegmentKind.SLEEP]: PORTFOLIO_I18N_KEYS.forecastSleepLabel,
  [ForecastSegmentKind.EVENTS]: PORTFOLIO_I18N_KEYS.forecastEventsLabel,
  [ForecastSegmentKind.ROUTINES]: PORTFOLIO_I18N_KEYS.forecastRoutinesLabel,
  [ForecastSegmentKind.FREE]: PORTFOLIO_I18N_KEYS.forecastFreeLabel,
  [ForecastSegmentKind.PROJECT]: null
};

/** Thresholds for coloring TOP score chips on portfolio cards. */
export const TOP_SCORE_TIER = {
  HIGH: 8,
  MEDIUM: 5
} as const;

export const getPortfolioWeekdayLabelKey = (
  dayKey: PortfolioWeekdayI18nKey
): string => `portfolio.weekday_${dayKey}`;

export const MATCHABLE_PRESETS = [
  CapacityPresetId.FULL_WEEK_8,
  CapacityPresetId.WORKWEEK_8,
  CapacityPresetId.WORKWEEK_6,
  CapacityPresetId.LIGHT_4,
  CapacityPresetId.FULL_WEEK_4
] as const;

export const DEFAULT_PROJECT_STATUS = {
  TO_DO: {
    label: 'To Do',
    color: DEFAULT_PROJECT_STATUS_COLORS.TO_DO,
    order: 0,
    should_complete_task: false
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: DEFAULT_PROJECT_STATUS_COLORS.IN_PROGRESS,
    order: 1,
    should_complete_task: false
  },
  DONE: {
    label: 'Done',
    color: DEFAULT_PROJECT_STATUS_COLORS.DONE,
    order: 2,
    should_complete_task: true
  }
} as const;

export const DND_ITEM_OPACITY = {
  DRAGGING: 0.5,
  NOT_DRAGGING: 1
} as const;

/** CSS custom property for status-tinted list/kanban surfaces. */
export const PROJECT_STATUS_COLOR_CSS_VAR = '--project-status-color';

/**
 * Theme-aware color-mix surfaces. Percentages are baked into the class strings
 * so Tailwind JIT can detect them; keep light < dark slightly for visibility.
 */
export const PROJECT_STATUS_TINT_CLASSES = {
  header:
    '!bg-[color-mix(in_srgb,var(--project-status-color)_18%,white)] dark:!bg-[color-mix(in_srgb,var(--project-status-color)_24%,#25221E)]',
  body:
    '!bg-[color-mix(in_srgb,var(--project-status-color)_10%,white)] dark:!bg-[color-mix(in_srgb,var(--project-status-color)_14%,#25221E)]',
  /** Light: soft status wash. Dark: solid surface so icons/text stay readable. */
  card:
    '!bg-[color-mix(in_srgb,var(--project-status-color)_8%,white)] dark:!bg-secondary-dark-200 border-secondary-400/30 dark:border-secondary-dark-400 border-l-[3px] border-l-[color:var(--project-status-color)]',
  title:
    'text-[color-mix(in_srgb,var(--project-status-color)_62%,#111827)] dark:text-[color-mix(in_srgb,var(--project-status-color)_50%,#F3F4F6)]'
} as const;

export const NEW_PROJECT_DESCRIPTION_ROWS = 3;
